import { TypedDocumentNode } from '@apollo/client';
import { useMutation, useQuery } from '@apollo/client/react';
import {
  Door,
  Location,
  Mutation,
  MutationAddLocationArgs,
  MutationAssignToAccessGrantsArgs,
  MutationUpdateLocationArgs,
  Query,
  VisitorGroup
} from 'graphql/generated/graphqlTypes';
import { ADD_DEVICE_GROUP, ASSIGN_DEVICE_GROUP_TO_ACCESS_GRANTS, UPDATE_DEVICE_GROUP } from 'graphql/v2/mutations';
import {
  GET_ALL_DOORS_FOR_DEVICE_GROUPS,
  GET_ALL_VISITORS_GROUPS_WITHOUT_NESTED_RESOLVERS,
  GET_DOORS_FOR_TENANT,
  GET_LOCATIONS_WITHOUT_NESTED_RESOLVERS
} from 'graphql/v2/queries';
import { BaseHookProps } from 'hooks/shared/types';
import { useRegisterBackgroundRefetch } from 'hooks/useBackgroundRefetch/useBackgroundRefetch';
import useTranslation from 'hooks/useTranslation/useTranslation';
import { useEffect } from 'react';
import { getAllAccessGrantsOfVisitorGroup } from 'utils/VisitorGroup/VisitorGroup';
import { resolveLocationDoors } from 'v2/utils/lock';

export type DeviceGroupRow = Location & { userGroups: VisitorGroup[] };

// Typing the documents (instead of passing generics to the Apollo hooks) avoids the deprecated hook signatures.
// Only the door (device) ids are selected for the locations (device groups), so the door names are joined from the
// doors query below.
const GET_LOCATIONS_WITHOUT_NESTED_RESOLVERS_TYPED: TypedDocumentNode<Pick<Query, 'getLocationsForTenant'>> =
  GET_LOCATIONS_WITHOUT_NESTED_RESOLVERS;
// Only ids are selected for the members and access grant locations/doors, so read `memberIds`, `locationIds`
// and `doorIds` from these groups instead of `members`, `locations` and `doors`.
const GET_ALL_VISITORS_GROUPS_WITHOUT_NESTED_RESOLVERS_TYPED: TypedDocumentNode<
  Pick<Query, 'getVisitorGroupsForTenant'>
> = GET_ALL_VISITORS_GROUPS_WITHOUT_NESTED_RESOLVERS;
const GET_ALL_DOORS_FOR_DEVICE_GROUPS_TYPED: TypedDocumentNode<Pick<Query, 'getDoorsForTenant'>> =
  GET_ALL_DOORS_FOR_DEVICE_GROUPS;
const ADD_DEVICE_GROUP_TYPED: TypedDocumentNode<
  Pick<Mutation, 'addLocation'>,
  MutationAddLocationArgs
> = ADD_DEVICE_GROUP;
const ASSIGN_DEVICE_GROUP_TO_ACCESS_GRANTS_TYPED: TypedDocumentNode<
  Pick<Mutation, 'assignToAccessGrants'>,
  MutationAssignToAccessGrantsArgs
> = ASSIGN_DEVICE_GROUP_TO_ACCESS_GRANTS;
const UPDATE_DEVICE_GROUP_TYPED: TypedDocumentNode<
  Pick<Mutation, 'updateLocation'>,
  MutationUpdateLocationArgs
> = UPDATE_DEVICE_GROUP;

const useDeviceGroups = ({ handleFetchError }: BaseHookProps) => {
  const { translation } = useTranslation();
  const {
    loading: deviceGroupsLoading,
    error: deviceGroupsError,
    data: deviceGroupsData
  } = useQuery(GET_LOCATIONS_WITHOUT_NESTED_RESOLVERS_TYPED, {
    notifyOnNetworkStatusChange: true
  });

  const {
    loading: visitorGroupsLoading,
    error: visitorGroupsError,
    data: visitorGroupsData
  } = useQuery(GET_ALL_VISITORS_GROUPS_WITHOUT_NESTED_RESOLVERS_TYPED, {
    notifyOnNetworkStatusChange: true
  });

  const {
    loading: doorsLoading,
    error: doorsError,
    data: doorsData
  } = useQuery(GET_ALL_DOORS_FOR_DEVICE_GROUPS_TYPED, {
    notifyOnNetworkStatusChange: true
  });

  const loading =
    (deviceGroupsLoading && !deviceGroupsData) ||
    (visitorGroupsLoading && !visitorGroupsData) ||
    (doorsLoading && !doorsData);

  // `loading` is used instead of the network status because a failed refetch leaves the status on `error`, which would
  // keep the "refreshing" indicator on until the next success.
  const isRefetchingDeviceGroups =
    !loading &&
    ((!!deviceGroupsData && deviceGroupsLoading) ||
      (!!visitorGroupsData && visitorGroupsLoading) ||
      (!!doorsData && doorsLoading));

  useRegisterBackgroundRefetch(isRefetchingDeviceGroups);

  useEffect(() => {
    if (deviceGroupsError) {
      handleFetchError(translation.device_group_error_fetching);
      console.error(deviceGroupsError);
    }
  }, [deviceGroupsError]);

  useEffect(() => {
    if (visitorGroupsError) {
      handleFetchError(translation.device_group_error_fetching_user_groups);
      console.error(visitorGroupsError);
    }
  }, [visitorGroupsError]);

  useEffect(() => {
    if (doorsError) {
      handleFetchError(translation.devices_error_fetching);
      console.error(doorsError);
    }
  }, [doorsError]);

  const allLocations = deviceGroupsData?.getLocationsForTenant ?? [];
  const allVisitorGroups = visitorGroupsData?.getVisitorGroupsForTenant ?? [];
  const allDoors: Door[] = doorsData?.getDoorsForTenant ?? [];

  const doorsById = new Map(allDoors.map((door) => [door.id, door]));

  const deviceGroupsWithUserGroups: DeviceGroupRow[] = allLocations.map((location) => {
    const userGroups = allVisitorGroups.filter((group) =>
      group.accessGrants.some((grant) => grant.locationIds.includes(location.id))
    );
    return { ...location, doors: resolveLocationDoors(location, doorsById), userGroups };
  });

  const [addDeviceGroupMutation, { loading: addDeviceGroupLoading, error: addDeviceGroupError }] = useMutation(
    ADD_DEVICE_GROUP_TYPED,
    {
      // The new location (device group) isn't in the cached locations list yet, so the list is refetched.
      refetchQueries: [
        { query: GET_LOCATIONS_WITHOUT_NESTED_RESOLVERS },
        { query: GET_ALL_VISITORS_GROUPS_WITHOUT_NESTED_RESOLVERS }
      ],
      awaitRefetchQueries: true
    }
  );

  const [assignToAccessGrantsMutation, { error: assignError }] = useMutation(
    ASSIGN_DEVICE_GROUP_TO_ACCESS_GRANTS_TYPED,
    {
      refetchQueries: [{ query: GET_ALL_VISITORS_GROUPS_WITHOUT_NESTED_RESOLVERS }],
      awaitRefetchQueries: true
    }
  );

  // The mutation result updates the cached location's (device group's) name and doors (devices). The doors are
  // refetched so their `locationIds` include the changed location.
  const [updateDeviceGroupMutation, { error: updateDeviceGroupError }] = useMutation(UPDATE_DEVICE_GROUP_TYPED, {
    refetchQueries: [{ query: GET_DOORS_FOR_TENANT }],
    awaitRefetchQueries: true
  });

  useEffect(() => {
    if (addDeviceGroupError) {
      handleFetchError(addDeviceGroupError.message);
      console.error(addDeviceGroupError);
    }
  }, [addDeviceGroupError]);

  useEffect(() => {
    if (assignError) {
      handleFetchError(assignError.message);
      console.error(assignError);
    }
  }, [assignError]);

  useEffect(() => {
    if (updateDeviceGroupError) {
      handleFetchError(updateDeviceGroupError.message);
      console.error(updateDeviceGroupError);
    }
  }, [updateDeviceGroupError]);

  const createDeviceGroup = async (
    name: string
  ): Promise<{ success: boolean; groupId?: string; errorMessage?: string }> => {
    try {
      const result = await addDeviceGroupMutation({
        variables: { location: { name, doorIds: [] } }
      });
      const groupId = result.data?.addLocation?.id;
      if (!groupId) return { success: false, errorMessage: 'Failed to create device group' };
      return { success: true, groupId };
    } catch (err) {
      return { success: false, errorMessage: err instanceof Error ? err.message : 'An error occurred' };
    }
  };

  const assignUserGroupsToDeviceGroup = async (
    groupId: string,
    visitorGroupIds: string[]
  ): Promise<{ success: boolean; errorMessage?: string }> => {
    try {
      const selectedGroups = allVisitorGroups.filter((group) => visitorGroupIds.includes(group.id));
      const accessGrantIds = selectedGroups.flatMap((group) =>
        getAllAccessGrantsOfVisitorGroup(group).map((grant) => grant.id)
      );
      if (accessGrantIds.length > 0) {
        await assignToAccessGrantsMutation({
          variables: { accessGrantIds, doorIds: [], locationIds: [groupId] }
        });
      }
      return { success: true };
    } catch (err) {
      return { success: false, errorMessage: err instanceof Error ? err.message : 'An error occurred' };
    }
  };

  const updateDeviceGroupWithDoors = async (
    groupId: string,
    groupName: string,
    doorIds: string[]
  ): Promise<{ success: boolean; errorMessage?: string }> => {
    try {
      await updateDeviceGroupMutation({
        variables: { id: groupId, location: { name: groupName, doorIds } }
      });
      return { success: true };
    } catch (err) {
      return { success: false, errorMessage: err instanceof Error ? err.message : 'An error occurred' };
    }
  };

  return {
    deviceGroupsWithUserGroups,
    allDoors,
    allVisitorGroups,
    loading,
    createDeviceGroup,
    assignUserGroupsToDeviceGroup,
    updateDeviceGroupWithDoors,
    addDeviceGroupLoading,
    isRefetchingDeviceGroups
  };
};

export default useDeviceGroups;
